import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { UPDATE_INVENTORY_TYPE } from 'components/MenuNew/hooks/useInventoryForm';
import useInventoryActions from 'components/MenuNew/hooks/useInventoryActions';
import InventorySku from 'components/MenuNew/FormView/Inventory/components/InventorySku';
import InventoryUnit from 'components/MenuNew/FormView/Inventory/components/InventoryUnit';
import InventoryStock from 'components/MenuNew/FormView/Inventory/components/InventoryStock';
import InventoryBarcodesForm from 'components/MenuNew/FormView/Inventory/components/InventoryBarcodesForm';
import DuplicateBarcodeModal from 'components/Inventory/DuplicateBarcodeModal';

const InventoryActions = ({
  open,
  onClose,
  inventoryFormMethods,
  currentType,
  selectedInventory,
  uuid,
  productId,
}) => {
  const { t } = useTranslation();
  const {
    // barcode number
    setBarcodeNumber,
    handleSaveBarcodeNumber,
    duplicateBarcodeModalOpen,
    duplicateItems,
    handleContinueWithDuplicate,
    handleCancelDuplicate,

    // sku
    handleChangeSku,
    handleSaveSku,
    sku,
    error,

    // unit
    handleSelect,
    unit,
    handleSaveUnit,

    // Stock
    stock,
    handleChangeStock,
    handleUpdateStock,
    stockError,

    // general validations
    isDirty,
    valid,
    setIsDirty,
    setIsValid,
    isLoading,
  } = useInventoryActions(
    selectedInventory,
    open,
    onClose,
    uuid,
    inventoryFormMethods,
    currentType,
    productId
  );

  // Content map for different inventory types
  const contentMap = {
    [UPDATE_INVENTORY_TYPE.BARCODE_NUMBER]: (
      <Box sx={{ mt: 3 }}>
        <InventoryBarcodesForm
          value={selectedInventory?.data}
          setBarcodeNumber={setBarcodeNumber}
          setIsDirty={setIsDirty}
          setIsValid={setIsValid}
        />
      </Box>
    ),
    [UPDATE_INVENTORY_TYPE.SKU]: (
      <Box sx={{ mt: 3 }}>
        <InventorySku
          sku={sku}
          handleChangeSku={handleChangeSku}
          error={error}
        />
      </Box>
    ),
    [UPDATE_INVENTORY_TYPE.UNIT]: (
      <Box sx={{ mt: 3 }}>
        <InventoryUnit unit={unit} handleSelect={handleSelect} />
      </Box>
    ),
    [UPDATE_INVENTORY_TYPE.STOCK]: (
      <Box sx={{ mt: 3 }}>
        <InventoryStock
          stock={stock}
          handleChangeStock={handleChangeStock}
          stockError={stockError}
        />
      </Box>
    ),
  };

  // Action map for different inventory types
  const actionMap = {
    [UPDATE_INVENTORY_TYPE.BARCODE_NUMBER]: handleSaveBarcodeNumber,
    [UPDATE_INVENTORY_TYPE.SKU]: handleSaveSku,
    [UPDATE_INVENTORY_TYPE.UNIT]: handleSaveUnit,
    [UPDATE_INVENTORY_TYPE.STOCK]: handleUpdateStock,
  };

  // Button text map for consistency
  const buttonTextMap = {
    remove: 'Remove',
    add: 'Add',
  };

  // Get content, action, and button text
  const renderedContent = contentMap[selectedInventory?.type] || null;
  const handleSave = actionMap[selectedInventory?.type] || (() => {});
  const buttonText = buttonTextMap[currentType] || 'Save';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>{t(selectedInventory.title)}</DialogTitle>

      <DialogContent>{renderedContent}</DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          sx={{ borderRadius: 1.5 }}
        >
          {t('Cancel')}
        </Button>
        <LoadingButton
          onClick={handleSave}
          disabled={!isDirty || !valid || isLoading}
          loading={isLoading}
          color="primary"
          variant="contained"
          sx={{ borderRadius: 1.5, ml: 1 }}
        >
          {t(buttonText)}
        </LoadingButton>
      </DialogActions>

      {/* Duplicate Barcode Modal */}
      <DuplicateBarcodeModal
        open={duplicateBarcodeModalOpen}
        onClose={handleCancelDuplicate}
        onContinue={handleContinueWithDuplicate}
        duplicateItems={duplicateItems}
      />
    </Dialog>
  );
};

export default InventoryActions;
